import React, { memo } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Tooltip,
} from '@mui/material';
import {
    CheckBoxOutlineBlank,
    CheckBox,
} from '@mui/icons-material';
import { isLockedTrackerStatus } from '../../utils/trackerStatusLock';

const formatOwners = (value) => {
    if (Array.isArray(value)) {
        return value
            .map((owner) => {
                if (typeof owner === 'object') {
                    return owner.name || owner.email || 'N/A';
                }
                return owner;
            })
            .join(', ');
    }
    return value || 'No owners';
};

const formatManagers = (value) => {
    if (Array.isArray(value)) {
        return value
            .map((mgr) => {
                if (typeof mgr === 'object') {
                    return mgr.email || mgr.name || 'N/A';
                }
                return mgr;
            })
            .join(', ');
    }
    return value || 'No managers';
};

const TrackersTable = ({
    rows,
    displayedRows,
    selectedTrackers,
    onRowSelect,
    onToggleSelectAll,
    allSelected,
    someSelected,
    isAdmin,
}) => {
    return (
        <TableContainer
            component={Paper}
            sx={{
                mt: 2,
                borderRadius: 2,
                boxShadow: 3,
                overflow: 'auto',
                maxHeight: '55vh',      // 👈 keeps the table from taking over the page
            }}
        >
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell
                            padding="checkbox"
                            sx={{
                                background: (theme) =>
                                    `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                borderBottom: '2px solid',
                                borderColor: 'primary.main',
                                color: 'common.white',
                            }}
                        >
                            <Checkbox
                                icon={<CheckBoxOutlineBlank fontSize="small" />}
                                checkedIcon={<CheckBox fontSize="small" />}
                                size="small"
                                sx={{ color: 'white' }}
                                checked={allSelected}
                                indeterminate={someSelected}
                                onChange={onToggleSelectAll}
                            />
                        </TableCell>
                        {[
                            'UIN',
                            'Activity',
                            'Law Area',
                            'Status',
                            'Due Date',
                            'Act or Rule',
                            'Owners',
                            'Managers',
                        ].map((header) => (
                            <TableCell
                                key={header}
                                sx={{
                                    background: (theme) =>
                                        `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                    color: 'white',
                                    fontWeight: 'bold',
                                    borderBottom: '2px solid',
                                    borderColor: 'primary.main',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {header}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {displayedRows.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={9} align="center">
                                No results found!
                            </TableCell>
                        </TableRow>
                    ) : (
                        displayedRows.map((row) => {
                            const rowId = row._id || row.uniqueIdentifier;
                            const isSelected = selectedTrackers.includes(rowId);
                            const isLockedForNonAdmin = !isAdmin && isLockedTrackerStatus(row.status);

                            return (
                                <TableRow
                                    key={rowId}
                                    hover
                                    sx={{
                                        '&:nth-of-type(even)': {
                                            backgroundColor: 'grey.50',
                                        },
                                        opacity: isLockedForNonAdmin ? 0.75 : 1,
                                        transition: 'background-color 0.15s ease',
                                    }}
                                >
                                    <TableCell
                                        sx={{ borderBottom: '1px solid #eee' }}
                                        padding="checkbox"
                                    >
                                        <Tooltip
                                            title={
                                                isLockedForNonAdmin
                                                    ? 'Locked tracker. Only admin can reassign.'
                                                    : ''
                                            }
                                        >
                                            <span>
                                                <Checkbox
                                                    size="small"
                                                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                                                    checkedIcon={<CheckBox fontSize="small" />}
                                                    checked={isSelected}
                                                    onChange={() => onRowSelect(rowId)}
                                                    disabled={isLockedForNonAdmin}
                                                />
                                            </span>
                                        </Tooltip>
                                    </TableCell>

                                    <TableCell sx={{ borderBottom: '1px solid #eee' }}>
                                        {row.uniqueIdentifier || 'N/A'}
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #eee' }}>
                                        {row.complianceActivity}
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #eee' }}>
                                        {row.lawArea}
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #eee' }}>
                                        {row.status}
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #eee' }}>
                                        {row.dueDate
                                            ? new Date(row.dueDate).toLocaleDateString()
                                            : 'No Due Date'}
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #eee' }}>
                                        {row.actOrRule}
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #eee' }}>
                                        {formatOwners(row?.trackerOwner)}
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #eee' }}>
                                        {formatManagers(row?.trackerManager)}
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default memo(TrackersTable);
